import axios from "axios";
import { getJwt, clearAuthStorage } from "./session";

// Pick the right backend URL.
//
// • In dev / preview, REACT_APP_BACKEND_URL points to the dev / preview backend
//   and `window.location.origin` matches it, so we use that.
//
// • In production with a custom domain (e.g. https://nestvillagellc.app), the
//   build pipeline bakes the AUTO-GENERATED deployment URL (e.g.
//   https://*.emergent.host) into REACT_APP_BACKEND_URL. If the user visits
//   the CUSTOM domain instead, every API call becomes cross-origin, which
//   breaks cookie-based sessions in Safari and produces flaky behavior across
//   browsers. The Kubernetes ingress on the custom domain ALREADY routes
//   `/api/*` to the same backend, so we can just call same-origin and avoid
//   the cross-origin problem entirely.
function pickBackendBase() {
  const env = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return env;

  const pageOrigin = window.location.origin;

  // If the current page origin matches the env URL's origin → use env (covers
  // preview, local dev, and production-on-the-baked-host).
  try {
    const envOrigin = new URL(env).origin;
    if (pageOrigin === envOrigin) return env;
  } catch (err) {
    // REACT_APP_BACKEND_URL is not parseable as a URL. Surface for diagnostics
    // and fall through to same-origin (the safer default).
    // eslint-disable-next-line no-console
    console.warn("api: REACT_APP_BACKEND_URL is malformed (", env, ") —", err?.message || err);
  }

  // Different origin (typical when a custom domain is mapped to the same
  // deployment). Use same-origin so cookies, CORS, and same-site rules all
  // line up. The backend is reachable at /api on the same host because the
  // ingress proxies /api/* through to FastAPI.
  return pageOrigin;
}

const BACKEND_URL = pickBackendBase();
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getJwt();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Endpoints that should NOT trigger an automatic logout on 401.
// • /auth/login: returns 401 for bad password — that's not a session expiry.
// • /auth/forgot-password & /auth/reset-password: same reason.
// • /auth/me: bootstrap call. AuthProvider handles a 401 here gracefully
//   (sets user=null) and the public landing page must remain reachable for
//   unauthenticated visitors.
const SKIP_401_REDIRECT = ["/auth/login", "/auth/forgot-password", "/auth/reset-password", "/auth/me"];

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const path = window.location.pathname;
    // Don't redirect away from public unauthenticated routes (landing, login, etc.)
    const isOnPublicRoute = path === "/" || path.startsWith("/login")
      || path.startsWith("/forgot-password") || path.startsWith("/reset-password");
    const isOAuthCallback = window.location.hash.includes("session_id=");
    const isExempt = SKIP_401_REDIRECT.some((p) => url.endsWith(p));

    if (status === 401 && !isOnPublicRoute && !isOAuthCallback && !isExempt) {
      // Token expired / invalid: scrub all auth state and bounce to /login
      // with a query param so the login page can show a friendly toast.
      // (URL param is more reliable than sessionStorage for cross-navigation
      //  hand-offs because it survives hard reloads and isn't cleared by any
      //  intermediate code paths.)
      clearAuthStorage();
      window.location.href = "/login?reason=expired";
    }
    return Promise.reject(err);
  }
);
